import React from 'react';

import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { iota } from '@utils/types';
import clsx from 'clsx';

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexInline,
    '& > * + *': {
      marginLeft: '0.5rem',
    },
  },
  item: {
    ...common.smallerText,
    ...common.flexCenter,
    ...common.baseButton,
    padding: '6px 11px',
    border: '1px solid #DDDEE1',
    borderRadius: 2,
    color: theme.palette.text.secondary,
    '&$selected': {
      color: theme.palette.secondary.contrastText,
      backgroundColor: theme.palette.primary.main,
      cursor: 'initial',
      userSelect: 'initial',
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
      },
    },
    '&:hover': {
      backgroundColor: theme.palette.background.default,
    },
    '&$disabled': {
      cursor: 'not-allowed',
      '&:hover': {
        backgroundColor: 'initial',
      },
    },
  },
  selected: {},
  disabled: {},
}));

export interface PaginationProps {
  page: number;
  count: number;
  maxDisplayed?: number;
  onChange?: (page: number) => void;
}

export const Pagination: React.FC<PaginationProps> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { count, page, maxDisplayed = 5, onChange } = props;

  const renderPaginationItem = (i: number) => {
    const onClick = i !== page ? () => onChange?.(i) : undefined;

    return (
      <div key={i} className={clsx(classes.item, i === page && classes.selected)} onClick={onClick}>
        {i + 1}
      </div>
    );
  };

  const renderEllipsis = () => <div>...</div>;

  const getRange = (): [number, number] => {
    if (page - Math.floor(maxDisplayed / 2) <= 1) {
      return [0, Math.min(count, maxDisplayed)];
    }
    if (page + Math.floor((maxDisplayed + 1) / 2) <= count - 2) {
      return [page - Math.floor(maxDisplayed / 2), page + Math.floor((maxDisplayed + 1) / 2)];
    }
    return [Math.max(0, count - Math.floor(maxDisplayed)), count];
  };

  const [begin, end] = getRange();

  return (
    <div className={clsx(classes.container)}>
      <div
        className={clsx(classes.item, page <= 0 && classes.disabled)}
        onClick={page > 0 ? () => onChange?.(page - 1) : undefined}
      >
        <KeyboardArrowLeftIcon viewBox="8 8 8 8" style={{ fontSize: '1rem', width: 5 }} />
      </div>

      {begin > 0 && renderPaginationItem(0)}

      {begin > 1 && renderEllipsis()}

      {iota(end - begin).map((i) => renderPaginationItem(i + begin))}

      {end < count - 1 && renderEllipsis()}

      {end < count && renderPaginationItem(count - 1)}

      <div
        className={clsx(classes.item, page >= count - 1 && classes.disabled)}
        onClick={page < count - 1 ? () => onChange?.(page + 1) : undefined}
      >
        <KeyboardArrowRightIcon viewBox="8 8 8 8" style={{ fontSize: '1rem', width: 5 }} />
      </div>
    </div>
  );
};
